/**
 * Client-side format checks for values the server rejects outright.
 *
 * Screens that only checked "is it empty?" handed a malformed value straight to the API,
 * which answered with a failure the interceptor renders as the generic
 * "An unexpected error occurred while processing your request." toast — the user is told
 * nothing about which field is wrong (BUG-049). These predicates let a screen name the
 * offending field before any request is made.
 *
 * The patterns are the ones already proven on the Preference Whitelist screen
 * (preference-whitelist.component.ts), lifted here so every screen accepts and rejects
 * exactly the same inputs instead of each growing its own regex.
 */

/** Strict IPv4: four 0-255 octets, anchored, nothing before or after. */
const IPV4_PATTERN: RegExp =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

/**
 * Bare DNS domain: dot-separated labels plus an alphabetic TLD. No scheme, port or path.
 *
 * Letters are matched as Unicode, not `a-z`: an internationalized domain (`café.com`) is a
 * legal sending domain, and an ASCII-only pattern would refuse it in the browser even
 * though it reached the server fine before this check existed. Rejecting a domain a client
 * actually owns makes the field unusable for them, which is worse than passing an odd
 * value through to the API.
 */
const DOMAIN_PATTERN: RegExp =
  /^(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?\.)+\p{L}{2,}$/u;

/** Longest legal fully-qualified domain name (RFC 1035). */
const MAX_DOMAIN_LENGTH: number = 253;

/**
 * True when the value is a single, complete IPv4 address.
 *
 * Deliberately stricter than a bare `(\d{1,3}\.){3}\d{1,3}` test: that accepts
 * `999.999.999.999` (each octet still matches `\d{1,3}`) and, being unanchored, also
 * accepts an address buried in surrounding junk.
 *
 * @param value The raw field value; surrounding whitespace is ignored.
 */
export function isValidIpv4Address(value: string | undefined): boolean {
  return IPV4_PATTERN.test((value ?? '').trim());
}

/**
 * True when the value is a bare sending domain such as `mail.example.com`.
 *
 * WebUrlValidator cannot be reused here: it requires an `http(s)://` prefix, which a
 * custom email domain must not carry.
 *
 * @param value The raw field value; surrounding whitespace is ignored.
 */
export function isValidEmailDomain(value: string | undefined): boolean {
  // A single trailing dot is the absolute (fully-qualified) form of the same name, so
  // `example.com.` and `example.com` name one domain. Drop the root label rather than
  // refusing the value, and measure the length against the relative form the way RFC 1035
  // counts it.
  const domain: string = (value ?? '').trim().replace(/\.$/, '');
  return domain.length > 0 && domain.length <= MAX_DOMAIN_LENGTH && DOMAIN_PATTERN.test(domain);
}
